import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { 
  Trash2, 
  Plus, 
  Image as ImageIcon,
  ArrowLeft,
  GripVertical,
  Upload,
  Link as LinkIcon
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sortable Image Item Component
const SortableImageItem = ({ image, index, onDelete, deletingId, getImageUrl }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`admin-gallery-item ${isDragging ? 'dragging' : ''}`}
      data-testid={`gallery-item-${index}`}
    >
      <div className="admin-item-order">
        <button
          className="drag-handle"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={20} />
        </button>
        <span className="order-number">{index + 1}</span>
      </div>
      
      <div className="admin-item-preview">
        <img src={getImageUrl(image.url)} alt={image.alt} />
      </div>
      
      <div className="admin-item-info">
        <p className="admin-item-alt">{image.alt}</p>
        <p className="admin-item-url">{image.url.substring(0, 50)}...</p>
      </div>
      
      <div className="admin-item-actions">
        <Button
          variant="destructive"
          size="sm"
          onClick={() => onDelete(image.id)}
          disabled={deletingId === image.id}
          className="admin-delete-btn"
          data-testid={`delete-${index}`}
        >
          {deletingId === image.id ? (
            <span className="deleting-spinner"></span>
          ) : (
            <Trash2 size={16} />
          )}
        </Button>
      </div>
    </div>
  );
};

const AdminGallery = () => {
  const { toast } = useToast();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newImage, setNewImage] = useState({ url: '', alt: '' });
  const [addingImage, setAddingImage] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'url'
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch gallery images
  const fetchImages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gallery`);
      if (response.ok) {
        const data = await response.json();
        setImages(data);
      }
    } catch (error) {
      console.error('Error fetching gallery images:', error);
      toast({
        title: "Error",
        description: "Failed to load gallery images",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: "Please select a JPG, PNG, GIF, or WebP image",
          variant: "destructive"
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  // Upload file
  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast({
        title: "Error",
        description: "Please select a file to upload",
        variant: "destructive"
      });
      return;
    }

    setAddingImage(true);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('alt', newImage.alt.trim() || selectedFile.name.replace(/\.[^/.]+$/, ''));

    try {
      const response = await fetch(`${API_URL}/api/gallery/upload`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Image uploaded to gallery"
        });
        setSelectedFile(null);
        setNewImage({ url: '', alt: '' });
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        fetchImages();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload image');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to upload image",
        variant: "destructive"
      });
    } finally {
      setAddingImage(false);
    }
  };

  // Add new image via URL
  const handleAddImage = async (e) => {
    e.preventDefault();
    if (!newImage.url.trim()) {
      toast({
        title: "Error",
        description: "Please enter an image URL",
        variant: "destructive"
      });
      return;
    }

    setAddingImage(true);
    try {
      const response = await fetch(`${API_URL}/api/gallery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: newImage.url.trim(),
          alt: newImage.alt.trim() || 'Gallery image'
        })
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Image added to gallery"
        });
        setNewImage({ url: '', alt: '' });
        fetchImages();
      } else {
        throw new Error('Failed to add image');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add image",
        variant: "destructive"
      });
    } finally {
      setAddingImage(false);
    }
  };

  // Delete image
  const handleDelete = async (imageId) => {
    setDeletingId(imageId);
    try {
      const response = await fetch(`${API_URL}/api/gallery/${imageId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast({
          title: "Deleted",
          description: "Image removed from gallery"
        });
        fetchImages();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete image",
        variant: "destructive"
      });
    } finally {
      setDeletingId(null);
    }
  };

  // Handle drag end for reordering
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = images.findIndex((img) => img.id === active.id);
    const newIndex = images.findIndex((img) => img.id === over.id);

    // Optimistically update UI
    const newImages = arrayMove(images, oldIndex, newIndex);
    setImages(newImages);

    // Get new order of IDs
    const newOrder = newImages.map(img => img.id);

    try {
      const response = await fetch(`${API_URL}/api/gallery/reorder/batch`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_ids: newOrder })
      });

      if (!response.ok) {
        throw new Error('Failed to reorder');
      }
      
      toast({
        title: "Reordered",
        description: "Gallery order updated"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reorder images",
        variant: "destructive"
      });
      fetchImages(); // Refresh to get correct order
    }
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="admin-loading">Loading gallery...</div>
      </div>
    );
  }

  // Helper to get full image URL
  const getImageUrl = (url) => {
    if (url.startsWith('/api/uploads/')) {
      return `${API_URL}${url}`;
    }
    return url;
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <Link to="/" className="admin-back-link">
          <ArrowLeft size={20} />
          <span>Back to Site</span>
        </Link>
        <h1 className="admin-title">Gallery Management</h1>
        <p className="admin-subtitle">{images.length} images in gallery • Drag to reorder</p>
      </div>

      {/* Add New Image Form */}
      <Card className="admin-add-card">
        <CardContent className="admin-add-content">
          <h2 className="admin-section-title">
            <Plus size={20} />
            Add New Image
          </h2>
          
          {/* Upload Mode Toggle */}
          <div className="admin-upload-toggle">
            <button
              type="button"
              className={`upload-toggle-btn ${uploadMode === 'file' ? 'active' : ''}`}
              onClick={() => setUploadMode('file')}
            >
              <Upload size={16} />
              Upload File
            </button>
            <button
              type="button"
              className={`upload-toggle-btn ${uploadMode === 'url' ? 'active' : ''}`}
              onClick={() => setUploadMode('url')}
            >
              <LinkIcon size={16} />
              Image URL
            </button>
          </div>

          {uploadMode === 'file' ? (
            /* File Upload Form */
            <form onSubmit={handleFileUpload} className="admin-add-form">
              <div className="admin-file-upload-area">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  className="admin-file-input"
                  id="file-upload"
                  data-testid="file-upload-input"
                />
                <label htmlFor="file-upload" className="admin-file-label">
                  {selectedFile ? (
                    <div className="file-selected">
                      <ImageIcon size={24} />
                      <span className="file-name">{selectedFile.name}</span>
                      <span className="file-size">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
                    </div>
                  ) : (
                    <div className="file-placeholder">
                      <Upload size={32} />
                      <span>Click to select an image or drag & drop</span>
                      <span className="file-hint">JPG, PNG, GIF, WebP supported</span>
                    </div>
                  )}
                </label>
              </div>
              <div className="admin-input-single">
                <Input
                  type="text"
                  placeholder="Alt text / Description (optional)"
                  value={newImage.alt}
                  onChange={(e) => setNewImage({ ...newImage, alt: e.target.value })}
                  className="admin-input"
                  data-testid="upload-image-alt"
                />
              </div>
              <Button 
                type="submit" 
                disabled={addingImage || !selectedFile}
                className="admin-add-btn"
                data-testid="upload-image-submit"
              >
                {addingImage ? 'Uploading...' : 'Upload Image'}
              </Button>
            </form>
          ) : (
            /* URL Form */
            <form onSubmit={handleAddImage} className="admin-add-form">
              <div className="admin-input-group">
                <Input
                  type="url"
                  placeholder="Image URL (https://...)"
                  value={newImage.url}
                  onChange={(e) => setNewImage({ ...newImage, url: e.target.value })}
                  className="admin-input"
                  data-testid="add-image-url"
                />
                <Input
                  type="text"
                  placeholder="Alt text (optional)"
                  value={newImage.alt}
                  onChange={(e) => setNewImage({ ...newImage, alt: e.target.value })}
                  className="admin-input"
                  data-testid="add-image-alt"
                />
              </div>
              <Button 
                type="submit" 
                disabled={addingImage}
                className="admin-add-btn"
                data-testid="add-image-submit"
              >
                {addingImage ? 'Adding...' : 'Add Image'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Image List with Drag and Drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={images.map(img => img.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="admin-gallery-list">
            {images.map((image, index) => (
              <SortableImageItem
                key={image.id}
                image={image}
                index={index}
                onDelete={handleDelete}
                deletingId={deletingId}
                getImageUrl={getImageUrl}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {images.length === 0 && (
        <div className="admin-empty">
          <ImageIcon size={48} />
          <p>No images in gallery</p>
          <p className="admin-empty-hint">Add your first image using the form above</p>
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
